// src/features/oio/errors/components/FatalErrorBanner.tsx
import { useState } from "react"
import { AlertTriangle, RotateCcw } from "lucide-react"
import { Button } from "@/components/ui/button"

interface FatalErrorBannerProps {
  message: string
  retryable: boolean
  onRetry: () => void
  onReport: () => Promise<void>
}

export function FatalErrorBanner({ message, retryable, onRetry, onReport }: FatalErrorBannerProps) {
  const [reported, setReported] = useState(false)

  async function handleReport() {
    await onReport()
    setReported(true)
  }

  return (
    <div className="mx-3 my-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-destructive">Something went wrong</p>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{message}</p>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        {retryable && (
          <Button
            variant="outline"
            size="sm"
            className="flex-1 gap-1.5 border-destructive/30 text-xs text-destructive hover:bg-destructive/10"
            onClick={onRetry}
          >
            <RotateCcw className="h-3 w-3" />
            Retry
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="flex-1 text-xs text-muted-foreground"
          onClick={handleReport}
          disabled={reported}
        >
          {reported ? "Issue reported to your admin team" : "Report issue"}
        </Button>
      </div>
    </div>
  )
}
